import {Loadable} from "jotai/vanilla/utils/loadable";
import {Company} from "../types/domain/company";
import {CompanyMemberRole, CompanyMemberSecurityAttributes} from "../types/domain/companyMember";
import {User, UserGlobalRole, UserSecurityAttributes} from "../types/domain/user";
import {DEMO_COMPANY_ID} from "./constants";
import {AccessFunctions} from "../types/accessControl";

/**
 * Checks if a given user has a Global User role included in a given permitted roles array
 */
export const isUserHaveAnyOfGlobalRoles = (user: Pick<User, "security">, roles: UserGlobalRole[]): boolean => {
    return roles.includes(user.security.role);
};

/**
 * Checks if a given user has a Company Member role (in a specified company) included in a given permitted roles array
 */
export const isUserHaveAnyOfCompanyRoles = (
    user: Pick<User, "companySecurity">,
    companyId: string,
    roles: CompanyMemberRole[],
): boolean => {
    const security = user.companySecurity.find((item) => item.companyId === companyId);

    return security !== undefined && roles.includes(security.role);
};

/**
 * Checks if a given user has a Company Member role (in a specified company or its parent companies) included in a given permitted roles array
 */
export const isUserHaveTransitivelyAnyOfCompanyRoles = (
    user: Pick<User, "companySecurity">,
    company: Pick<Company, "id" | "parentCompaniesIds">,
    roles: CompanyMemberRole[],
) => {
    const targetCompaniesChainIds = [...company.parentCompaniesIds, company.id];
    return user.companySecurity.some(
        (companySecurity) =>
            targetCompaniesChainIds.includes(companySecurity.companyId) && roles.includes(companySecurity.role),
    );
};

/**
 * Checks if a given user has a Company Member role (in any company) included in a given permitted roles array
 */
export const isUserHaveAnyRoleOfAnyCompany = (
    user: Pick<User, "companySecurity">,
    roles: CompanyMemberRole[],
): boolean => {
    return user.companySecurity.some((item) => roles.includes(item.role));
};

//Tanstack Query Version of Access Utils Closure, old one will be removed after refactoring
export const getAccessUtilsClosureTSQ = (
    authenticatedUserState: Loadable<Promise<User>>,
    activeCompanyQuery: {data?: Company; isSuccess: boolean},
    activeUserState: Loadable<Promise<User | undefined>>,
) => ({
    hasAnyGlobalRole: (roles: UserGlobalRole[]): boolean =>
        authenticatedUserState.state === "hasData" && isUserHaveAnyOfGlobalRoles(authenticatedUserState.data, roles),
    hasAnyActiveCompanyRole: (roles: CompanyMemberRole[]): boolean =>
        authenticatedUserState.state === "hasData" &&
        activeCompanyQuery.isSuccess &&
        activeCompanyQuery.data !== undefined &&
        isUserHaveAnyOfCompanyRoles(authenticatedUserState.data, activeCompanyQuery.data.id, roles),

    hasAnyActiveCompanyTransitiveRole: (roles: CompanyMemberRole[]): boolean =>
        authenticatedUserState.state === "hasData" &&
        activeCompanyQuery.isSuccess &&
        activeCompanyQuery.data !== undefined &&
        isUserHaveTransitivelyAnyOfCompanyRoles(authenticatedUserState.data, activeCompanyQuery.data, roles),

    isActiveDemoCompany: (): boolean =>
        activeCompanyQuery.isSuccess &&
        activeCompanyQuery.data !== undefined &&
        activeCompanyQuery.data.id === DEMO_COMPANY_ID,
    isActiveUserAuthenticatedUser: (): boolean =>
        activeUserState.state === "hasData" &&
        authenticatedUserState.state === "hasData" &&
        authenticatedUserState.data &&
        authenticatedUserState.data.id === activeUserState.data?.id,

    getGlobalAttribute: <T extends keyof UserSecurityAttributes>(
        attributeName: T,
    ): UserSecurityAttributes[T] | undefined =>
        authenticatedUserState.state === "hasData"
            ? authenticatedUserState.data.security.attributes[attributeName]
            : undefined,

    getActiveCompanyAttribute: <T extends keyof CompanyMemberSecurityAttributes>(
        attributeName: T,
    ): UserSecurityAttributes[T] | undefined =>
        authenticatedUserState.state === "hasData"
            ? authenticatedUserState.data.security.attributes[attributeName]
            : undefined,

    hasData: (): boolean =>
        authenticatedUserState.state === "hasData" &&
        activeCompanyQuery.isSuccess &&
        activeCompanyQuery.data !== undefined,
});

export const getAccessUtilsClosure = (
    authenticatedUserState: Loadable<Promise<User>>,
    activeCompanyState: Loadable<Promise<Company>>,
    activeUserState: Loadable<Promise<User | undefined>>,
) => ({
    hasAnyGlobalRole: (roles: UserGlobalRole[]): boolean =>
        authenticatedUserState.state === "hasData" && isUserHaveAnyOfGlobalRoles(authenticatedUserState.data, roles),

    hasAnyActiveCompanyRole: (roles: CompanyMemberRole[]): boolean =>
        authenticatedUserState.state === "hasData" &&
        activeCompanyState.state === "hasData" &&
        activeCompanyState.data !== undefined &&
        isUserHaveAnyOfCompanyRoles(authenticatedUserState.data, activeCompanyState.data.id, roles),

    hasAnyActiveCompanyTransitiveRole: (roles: CompanyMemberRole[]): boolean =>
        authenticatedUserState.state === "hasData" &&
        activeCompanyState.state === "hasData" &&
        activeCompanyState.data !== undefined &&
        isUserHaveTransitivelyAnyOfCompanyRoles(authenticatedUserState.data, activeCompanyState.data, roles),

    isActiveDemoCompany: (): boolean =>
        activeCompanyState.state === "hasData" &&
        activeCompanyState.data !== undefined &&
        activeCompanyState.data.id === DEMO_COMPANY_ID,

    isActiveUserAuthenticatedUser: (): boolean =>
        activeUserState.state === "hasData" &&
        authenticatedUserState.state === "hasData" &&
        authenticatedUserState.data &&
        authenticatedUserState.data.id === activeUserState.data?.id,

    getGlobalAttribute: <T extends keyof UserSecurityAttributes>(
        attributeName: T,
    ): UserSecurityAttributes[T] | undefined =>
        authenticatedUserState.state === "hasData"
            ? authenticatedUserState.data.security.attributes[attributeName]
            : undefined,

    getActiveCompanyAttribute: <T extends keyof CompanyMemberSecurityAttributes>(
        attributeName: T,
    ): UserSecurityAttributes[T] | undefined =>
        authenticatedUserState.state === "hasData"
            ? authenticatedUserState.data.security.attributes[attributeName]
            : undefined,

    hasData: (): boolean => authenticatedUserState.state === "hasData" && activeCompanyState.state === "hasData",
});

export const ALL_ACCESS_FUNCTIONS: AccessFunctions = {
    canViewOrganizationsPage: (auth) =>
        auth.hasAnyGlobalRole([UserGlobalRole.ADMIN, UserGlobalRole.CS, UserGlobalRole.HYDROSAT]) ||
        auth.hasAnyActiveCompanyTransitiveRole([
            CompanyMemberRole.VIEWER,
            CompanyMemberRole.EDITOR,
            CompanyMemberRole.MANAGER,
        ]) ||
        auth.isActiveDemoCompany(),

    canViewCompanyFarms: (auth) =>
        !auth.isActiveDemoCompany() && auth.hasAnyGlobalRole([UserGlobalRole.CS, UserGlobalRole.ADMIN]),

    canViewCompanyMembers: (auth) =>
        !auth.isActiveDemoCompany() &&
        (auth.hasAnyGlobalRole([UserGlobalRole.HYDROSAT, UserGlobalRole.CS, UserGlobalRole.ADMIN]) ||
            auth.hasAnyActiveCompanyTransitiveRole([CompanyMemberRole.EDITOR, CompanyMemberRole.MANAGER])),

    canViewCompanyOrders: (auth) =>
        !auth.isActiveDemoCompany() &&
        (auth.hasAnyGlobalRole([UserGlobalRole.HYDROSAT, UserGlobalRole.CS, UserGlobalRole.ADMIN]) ||
            auth.hasAnyActiveCompanyTransitiveRole([CompanyMemberRole.EDITOR, CompanyMemberRole.MANAGER])),

    canViewAdminPage: (auth) =>
        auth.hasAnyGlobalRole([UserGlobalRole.HYDROSAT, UserGlobalRole.CS, UserGlobalRole.ADMIN]),

    canViewCompanyBranding: (auth) =>
        !auth.isActiveDemoCompany() &&
        auth.hasAnyGlobalRole([UserGlobalRole.HYDROSAT, UserGlobalRole.CS, UserGlobalRole.ADMIN]),

    canEditCompanyDetails: (auth) =>
        !auth.isActiveDemoCompany() &&
        (auth.hasAnyGlobalRole([UserGlobalRole.HYDROSAT, UserGlobalRole.CS, UserGlobalRole.ADMIN]) ||
            auth.hasAnyActiveCompanyTransitiveRole([CompanyMemberRole.MANAGER])),

    canViewCompanyApiCredentials: (auth) =>
        !auth.isActiveDemoCompany() &&
        (auth.hasAnyGlobalRole([UserGlobalRole.HYDROSAT, UserGlobalRole.CS, UserGlobalRole.ADMIN]) ||
            auth.hasAnyActiveCompanyTransitiveRole([CompanyMemberRole.MANAGER])),

    canViewUserApiCredentials: (auth) =>
        auth.hasAnyGlobalRole([UserGlobalRole.CS, UserGlobalRole.ADMIN]) || auth.isActiveUserAuthenticatedUser(),

    canEnableBetaFeatures: (auth) =>
        auth.hasAnyGlobalRole([UserGlobalRole.HYDROSAT, UserGlobalRole.CS, UserGlobalRole.ADMIN]),

    canAssignManagerRole: (auth) =>
        auth.hasAnyGlobalRole([UserGlobalRole.HYDROSAT, UserGlobalRole.CS, UserGlobalRole.ADMIN]),

    canAddCompanyMember: (auth) =>
        auth.hasAnyGlobalRole([UserGlobalRole.ADMIN, UserGlobalRole.CS, UserGlobalRole.ADMIN]),

    canEditCompanyFarms: (auth) =>
        !auth.isActiveDemoCompany() &&
        (auth.hasAnyGlobalRole([UserGlobalRole.ADMIN, UserGlobalRole.CS]) ||
            auth.hasAnyActiveCompanyTransitiveRole([CompanyMemberRole.EDITOR, CompanyMemberRole.MANAGER])),

    canCreateNewField: (auth) =>
        !auth.isActiveDemoCompany() &&
        (auth.hasAnyGlobalRole([UserGlobalRole.ADMIN, UserGlobalRole.CS]) ||
            auth.hasAnyActiveCompanyTransitiveRole([CompanyMemberRole.EDITOR, CompanyMemberRole.MANAGER])),

    canCreateNewFarm: (auth) =>
        !auth.isActiveDemoCompany() &&
        (auth.hasAnyGlobalRole([UserGlobalRole.ADMIN, UserGlobalRole.CS]) ||
            auth.hasAnyActiveCompanyTransitiveRole([CompanyMemberRole.EDITOR, CompanyMemberRole.MANAGER])),

    canEditCompanyFields: (auth) =>
        !auth.isActiveDemoCompany() &&
        (auth.hasAnyGlobalRole([UserGlobalRole.ADMIN, UserGlobalRole.CS]) ||
            auth.hasAnyActiveCompanyTransitiveRole([CompanyMemberRole.EDITOR, CompanyMemberRole.MANAGER])),

    canAccessDataQualityAnalysis: (auth) =>
        auth.hasAnyGlobalRole([UserGlobalRole.HYDROSAT, UserGlobalRole.CS, UserGlobalRole.ADMIN]),

    canViewEtawCumulativeChart: (auth) =>
        auth.getGlobalAttribute("betaFeatures") === true || auth.getActiveCompanyAttribute("betaFeatures") === true,

    canUploadCompanyData: (auth) =>
        !auth.isActiveDemoCompany() &&
        (auth.hasAnyGlobalRole([UserGlobalRole.ADMIN, UserGlobalRole.CS]) ||
            auth.hasAnyActiveCompanyTransitiveRole([CompanyMemberRole.EDITOR, CompanyMemberRole.MANAGER])),

    canSetAdvancedCompanyProperties: (auth) => auth.hasAnyGlobalRole([UserGlobalRole.CS, UserGlobalRole.ADMIN]),

    canSetParentCompany: (auth) =>
        auth.hasAnyGlobalRole([UserGlobalRole.ADMIN, UserGlobalRole.CS]) ||
        auth.hasAnyActiveCompanyTransitiveRole([CompanyMemberRole.MANAGER]),

    canGetAllCompanies: (auth) => auth.hasAnyGlobalRole([UserGlobalRole.ADMIN, UserGlobalRole.CS]),

    canEditAnyOrder: (auth) => auth.hasAnyGlobalRole([UserGlobalRole.ADMIN, UserGlobalRole.CS]),
};
